<template>
  <div class="carfax-model-reconcile">
    <div class="notification is-info is-flex">
      <i class="fas fa-info-circle is-info fa-2x"></i>
      <div class="ml-10" data-test="carfax-reconcile-message">{{ message }}</div>
    </div>

    <!--⚠ ABOVE the table, not below it. The card caps at 480px, so a warning under the choices would
         drop below the fold exactly when the list is longest - which is precisely when the operator
         has most to weigh up. -->
    <div class="notification is-warning is-flex" v-if="notInCatalogueWarning"
         data-test="carfax-not-in-catalogue-warning">
      <i class="fas fa-exclamation-triangle is-warning fa-2x"></i>
      <div class="ml-10">{{ notInCatalogueWarning }}</div>
    </div>

    <table class="table is-fullwidth is-narrow carfax-model-choices" data-test="carfax-model-choices">
      <thead>
        <tr>
          <th></th>
          <th>Model</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.key" :data-test="'carfax-model-choice-' + row.key">
          <td>
            <div class="block">
              <!--⚠ Bound to a single key, not an array: one model is applied, so these behave as
                   radios while reading as the checkbox column that was asked for. -->
              <b-checkbox
                :value="chosenKey === row.key"
                :data-test="'carfax-model-check-' + row.key"
                @input="onChoose(row.key)">
              </b-checkbox>
            </div>
          </td>
          <td :class="row.inCatalogue ? 'has-text-success' : 'has-text-danger'">{{ row.name }}</td>
          <td>{{ row.source }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
/*
 * The CARFAX reconcile prompt: the message and the model choice table.
 *
 * ⚠ It deliberately carries NO field-by-field "This record / CARFAX" table. It had one, and it
 * made the prompt argue with itself: on VIN ZFF83CLA4K0248000 the message and the choice table both
 * read "812 Superfast" while the table underneath highlighted Model, Trim and Style as differences.
 * This prompt asks ONE question - which model should this record hold - and the answer is a row in
 * the table above. CARFAX's trim, style and year arrive as pop-fill chips once a row is applied,
 * and CarfaxVehicleComparison still renders where a full diff is what was asked for: the inline
 * variant behind the CARFAX match icon, and the read-only copy in ViewInventory.
 *
 * ⚠ Why a CHOICE and not a yes/no. CARFAX returns the model and the series as SEPARATE fields -
 * model "812", series "Superfast" - while the catalogue row is named "812 Superfast". The two halves
 * are never joined, so the lookup cannot match and the old prompt could only offer "apply 812 as free
 * text" or "no". A record filed as free text carries no catalogue link and drops out of model
 * reporting. This table lets the operator say "CARFAX means 812, and the row I want is 812 Superfast".
 *
 * ⚠ It OFFERS, it never picks. The current model is pre-checked, so Apply with no interaction changes
 * no model - the safe default. Ferrari 296 in 2024 is two catalogue rows and Alfa Giulia in 2020 is
 * thirteen, so choosing for the operator would be wrong far more often than right.
 *
 * ⚠ THAT SAFE DEFAULT WAS A CLAIM, NOT A FACT, until meeting change #15. The current row carried
 * model: null, which the apply path reads as "nothing ticked" and answers by applying CARFAX - so
 * Apply with no interaction did the one thing this comment promised it would not. It now carries
 * KEEP_CURRENT_MODEL, and applyCarfaxMakeModel returns on it.
 *
 * ⚠ ONE component for every surface. The prompt was hand-copied into EditInventory, NewInventory,
 * ApprovePpaAction, both RO service vehicle screens, a read-only copy in ViewInventory and an inline
 * copy in MakeModelSelector - seven renders, six of them copies, each carrying its own inline copy of
 * the same flag rename. That is how a dismiss handler came to be bound to a method that does not
 * exist on one of the hosts.
 */
import { readMakeModelFields, findActiveCatalogueModel, findCatalogueModelCandidates, findCatalogueModelGroupCandidates, buildModelReconcileMessage, buildNotInCatalogueWarning, buildCurrentNotInCatalogueWarning, tightKey, KEEP_CURRENT_MODEL } from '@/utils/carfax_make_model_resolution'

/*
 * ⚠ IMPORTED, not redefined. This file used to carry its own `squash` - same name as the one in
 * carfax_make_model_resolution, but uppercasing where that one does not, which is drift that had
 * already happened between two same-named helpers in two files.
 *
 * It is now the shared tight key, which also removes spaces: the catalogue's "S 580" and CARFAX's
 * "S580" are the same vehicle, so they must produce ONE row here rather than two, and a suggestion
 * spelled either way must still be recognised as matching what the record holds.
 */
const squash = tightKey

/*
 * Whether the learned suggestion is worth showing at all: it must exist, be named, and differ from
 * what the record already holds.
 *
 * ⚠ Shared deliberately. data() cannot read computed properties, so the pre-tick default and the row
 * push each need this test - and when they were two copies of the expression they drifted on the
 * first case that exercised them, suppressing the row while chosenKey still named it. That leaves
 * chosenRow null, which every host binds straight to its Apply-disabled flag.
 */
const suggestionIsOfferable = (suggestedModel, currentModelName) => {
  if (!suggestedModel) return false
  const suggestedName = squash(suggestedModel.name)
  return !!suggestedName && suggestedName !== squash(currentModelName)
}

export default {
  name: 'CarfaxModelReconcile',
  props: {
    // The record's current vehicle selection.
    selectedMakeModel: {
      type: Object,
      default: null
    },
    // The resolved CARFAX match, or its free-text values when nothing resolved.
    carfaxMakeModel: {
      type: Object,
      default: null
    },
    // The catalogue make, so the candidate rows can be found. Null is safe - the table just omits them.
    selectedMake: {
      type: Object,
      default: null
    },
    /*
     * What the offered row is called in the Source column. Defaults to the prompt's original source so
     * every existing host keeps its wording without passing anything.
     *
     * ⚠ The VIN decode raises this prompt BEFORE any CARFAX report exists, so labelling its row
     * "CARFAX" would name a report the operator has not seen and read as a fault in that report.
     */
    sourceLabel: {
      type: String,
      default: 'CARFAX'
    },
    /*
     * ⚠ A message passed IN rather than a flag switching between two sentences built in here. The
     * builders live in carfax_make_model_resolution beside the other three, and the reason that file
     * keeps them separate is recorded in it: overloading one of them silently blanked five live
     * prompts. A boolean here would recreate that fork inside this component instead.
     */
    /*
     * Candidate rows supplied by the host, overriding the group search this component does itself.
     *
     * ⚠ An OVERRIDE, not a replacement: left null, every existing host keeps the exact behaviour it
     * has. The typed path needs a substring search (findCatalogueModelsContaining) where the decode and
     * CARFAX prompts need the group search, and widening the shared matcher to serve both would have
     * changed three live prompts to fix one.
     */
    candidates: {
      type: Array,
      default: null
    },
    customMessage: {
      type: String,
      default: ''
    },
    /*
     * The learned-mapping suggestion from findLearnedMapping, or null when there is nothing to
     * suggest - which is the common case, so this defaults null and every existing host that passes
     * nothing behaves exactly as before.
     *
     * ⚠ NOT routed through `candidates`. That prop replaces the group/name union outright (see
     * candidateRows below), including with an empty list, so reusing it here would remove candidate
     * rows from every CARFAX host. It also hardcodes the Source column to 'VIN decode', which is the
     * one thing this row must not say.
     *
     * Shape: { model, name, trim, timesSeen, timesTotal, unanimous, label }
     */
    suggestedModel: {
      type: Object,
      default: null
    }
  },
  data: function () {
    return {
      /*
       * ⚠ 'It offers, it never picks' still holds everywhere except two cases (see initialChoice):
       * a suggestion the record ITSELF has produced unanimously, at least five times, and an empty
       * record whose CARFAX model is a catalogue model by exactly that name. There the answer is not
       * a judgement call and re-asking is the defect being fixed.
       *
       * A CONTESTED mapping is deliberately not pre-ticked however lopsided it looks - Grecale +
       * Modena was folded 28 times and dropped 14, and 911 + GT2 RS was 10 to 9. Pre-ticking a coin
       * flip turns Apply-without-reading into a data-changing action.
       */
      chosenKey: this.initialChoice()
    }
  },
  computed: {
    currentModelName: function () {
      const fields = readMakeModelFields(this.selectedMakeModel)
      return fields ? fields.model : ''
    },
    carfaxModelName: function () {
      const fields = readMakeModelFields(this.carfaxMakeModel)
      return fields ? fields.model : ''
    },
    message: function () {
      if (this.customMessage) return this.customMessage
      return buildModelReconcileMessage(this.carfaxModelName, this.currentModelName)
    },
    /*
     * ⚠ Follows the TICKED row, not the CARFAX one. The table already renders a non-catalogue model
     * red and says nothing about why, so the operator is left to know that red means free text with
     * no catalogue link. Keying this on chosenRow means it answers the question they actually have -
     * what happens if I apply THIS - and it clears itself the moment they tick a catalogue row.
     *
     * ⚠ The 'current' row needs different words. chosenKey defaults to it, and it is red on any
     * record already held as free text, so this fires on open for those - correctly. But applying it
     * changes nothing, so the apply-consequence sentence would describe an action that never happens.
     * That is true of KEEP_CURRENT_MODEL; it was NOT true of the null this row used to carry, which
     * applied CARFAX's model instead - meeting change #15.
     */
    notInCatalogueWarning: function () {
      const row = this.chosenRow
      if (!row || row.inCatalogue) return ''

      return row.key === 'current'
        ? buildCurrentNotInCatalogueWarning(row.name)
        : buildNotInCatalogueWarning(row.name)
    },
    /*
     * ⚠ Candidates come from the CARFAX name, not the record's. The record may already hold something
     * specific and unrelated; what the operator needs offered is the family CARFAX is pointing at.
     * findCatalogueModelCandidates is the existing matcher - year-filtered, Deleted rows dropped,
     * matching on series or on a "<group> " name prefix - and it returns nothing when a row already
     * matches outright, so an exact hit never produces a redundant list.
     *
     * ⚠ That last property is exactly what the GROUP search exists to get past. CARFAX sends the
     * model and series separately and the resolver joins them, so a row that matched outright leaves
     * only the joined name here ("911 Turbo S") - and searching on that reaches rows beneath it but
     * never its siblings. The group search is keyed on what CARFAX actually sent, so "911 Turbo" is
     * offered too.
     *
     * ⚠ THE TWO ARE UNIONED, NOT PREFERRED, and that is a fix rather than a flourish. The group
     * search used to win outright whenever it returned anything, and because sourceModelName and
     * sourceSeries are set on BOTH branches of buildCarfaxComparison it also fired on ratings that
     * resolved to nothing - where it does not add siblings, it REMOVES options. Ferrari model "296"
     * series "GTS" for 2024 offered "296 GTS" alone where the name search offered "296 GTB" and
     * "296 GTS", so the operator who wanted GTB could no longer pick it. That contradicts this
     * component's own rule: it offers, it never picks. Unioned, the group search can only ever ADD.
     *
     * ⚠ The dedupe below is load-bearing. The two finders are NESTED - both start from the same
     * year-filtered, non-Deleted set and keep the same group predicate, and the group finder then
     * adds one more condition - so wherever the name search does not early-return, the group result
     * is a strict SUBSET of it and every group row is a duplicate. Drop the dedupe and the Ferrari
     * case lists "296 GTS" twice.
     */
    candidateRows: function () {
      //⚠ A supplied list wins outright, including an empty one - the host has already searched and
      //found nothing, so falling back to either search here would contradict it.
      if (this.candidates !== null) return this.candidates

      const carfax = this.carfaxMakeModel
      const year = carfax ? carfax.productionYear : null

      const group = findCatalogueModelGroupCandidates(
        this.selectedMake, carfax && carfax.sourceModelName, carfax && carfax.sourceSeries, year)
      const named = findCatalogueModelCandidates(this.selectedMake, this.carfaxModelName, year) || []

      /*
       * Group first: those rows carry the series CARFAX actually reported, so they are the likeliest
       * pick. The rest of the family follows.
       *
       * ⚠ No null-row or missing-id guard, deliberately. Both finders filter foundMake.versions and
       * both already dereference each row - key(x.name) - so anything that would break here has
       * thrown before it arrives. Measured on the live catalogue: 152 makes, 1,953 versions, zero
       * null rows and zero rows without an id. Guarding it would be handling a case the input cannot
       * produce, and it was the only thing keeping this file's branch coverage down.
       */
      const seen = {}
      return group.concat(named).filter((row) => {
        if (seen[row.id]) return false
        seen[row.id] = true
        return true
      })
    },
    carfaxCatalogueMatch: function () {
      return this.findCarfaxCatalogueMatch()
    },
    rows: function () {
      const rows = []
      const seen = {}
      /*
       * ⚠ A free-text row does not block a catalogue row of the same name. The dedupe exists so one
       * model is not offered twice - but a free-text "SF90 XX STRADALE" and the catalogue row of that
       * name are different answers: only the second links the record. Dropping the catalogue row left
       * the operator able to re-pick only the free text (FP7489A, 2026-09-29).
       */
      const push = (key, name, source, inCatalogue, model) => {
        const fingerprint = squash(name)
        if (!fingerprint) return
        if (seen[fingerprint] !== undefined && !(inCatalogue && seen[fingerprint] === false)) return
        seen[fingerprint] = inCatalogue
        rows.push({ key: key, name: name, source: source, inCatalogue: inCatalogue, model: model })
      }
      const carfaxMatch = this.carfaxCatalogueMatch

      /*
       * ⚠ FIRST, and the order is load-bearing. push() fingerprints on squash(name) and silently
       * drops a repeat, and a learned suggestion's name is usually a name the group search also
       * produces. Pushed anywhere but first it is swallowed, and the model then appears under the
       * generic 'VIN decode' label with no evidence attached - the feature would look like it
       * simply had not loaded.
       *
       * ⚠ Suppressed when it agrees with what the record already holds: there is nothing to
       * correct, and offering it would push the real 'Currently selected' row out of the table.
       */
      if (suggestionIsOfferable(this.suggestedModel, this.currentModelName)) {
        push('suggested', this.suggestedModel.name, this.suggestedModel.label, true,
          this.suggestedModel.model)
      }
      //⚠ KEEP_CURRENT_MODEL, never null. Null is indistinguishable from "nothing ticked", which the
      //apply path answers by applying CARFAX - see the marker's own comment for what that cost.
      push('current', this.currentModelName, 'Currently selected',
        !!(this.selectedMakeModel && this.selectedMakeModel.modelId > 0), KEEP_CURRENT_MODEL)
      /*
       * ⚠ Linked when CARFAX's own lookup missed an active catalogue model of exactly this name for
       * CARFAX's year. The row then hands over THAT catalogue row, the same shape a candidate row
       * does, so applyCarfaxMakeModel applies it by id - the record lands on the row this prompt
       * showed, and CARFAX's trim and style still cascade from the host's carfaxMakeModel.
       *
       * ⚠ Not a CARFAX-shaped copy carrying modelId (code review, 2026-10-20). Apply ignores modelId
       * on the chosen model and looked the name up again at usableCarfaxYear(), which falls back to
       * the record's year - so a CARFAX year it rejects showed one row ticked and applied another.
       */
      push('carfax', this.carfaxModelName, this.sourceLabel,
        !!(this.carfaxMakeModel && this.carfaxMakeModel.modelId > 0) || !!carfaxMatch,
        carfaxMatch || this.carfaxMakeModel)
      this.candidateRows.forEach((candidate, index) => {
        //⚠ 'VIN decode', not 'Vehicle make/model data'. The row IS the decoder's answer, and the
        //old wording named the catalogue it was matched against rather than where it came from -
        //so the operator could not tell which source had suggested it. Meeting change #14.
        push('candidate-' + (candidate.id != null ? candidate.id : index), candidate.name,
          'VIN decode', true, candidate)
      })

      return rows
    },
    chosenRow: function () {
      return this.rows.find((x) => x.key === this.chosenKey) || null
    }
  },
  watch: {
    /*
     * Tells the host whether Apply has anything to apply, because the Apply button lives in the host's
     * footer on every surface rather than in here.
     *
     * ⚠ immediate, so the host learns the opening state without having to guess it. The two sources
     * open differently: the CARFAX prompt pre-checks the current model and is applyable at once, while
     * the decode prompt deliberately pre-checks nothing - there is no current model, and picking a
     * candidate for the operator is what this change exists to stop. (The exact-catalogue-name
     * pre-tick in initialChoice cannot reach it: a decoded name the catalogue holds outright is
     * resolved by the decode itself and never prompts.) Without this the decode footer
     * would open enabled and apply null, closing the prompt with the model still empty.
     *
     * ⚠ Watched rather than emitted from onChoose, because chosenRow also turns null->row when the
     * catalogue make arrives after the prompt renders.
     */
    chosenRow: {
      immediate: true,
      handler: function (newValue) {
        this.$emit('choice-changed', newValue !== null && newValue !== undefined)
      }
    }
  },
  methods: {
    /*
     * The opening tick. ⚠ A method, not a computed, because data() runs before computeds exist.
     *
     * 'It offers, it never picks' holds, with two cases where the answer is not a judgement call:
     * - a suggestion the record ITSELF has produced unanimously, at least five times. A CONTESTED
     *   mapping is deliberately not pre-ticked however lopsided it looks - Grecale + Modena was folded
     *   28 times and dropped 14, and 911 + GT2 RS was 10 to 9. Pre-ticking a coin flip turns
     *   Apply-without-reading into a data-changing action.
     * - a record holding NO model, where CARFAX's model is a catalogue model by exactly that name.
     *   There is nothing to keep, and ticking nothing only disabled Apply on the one obvious answer
     *   (FP7489A, 2026-09-29). A record that holds a model keeps 'current' - Apply alone still changes
     *   nothing there.
     */
    initialChoice: function () {
      const currentName = readMakeModelFields(this.selectedMakeModel).model
      if (suggestionIsOfferable(this.suggestedModel, currentName) && this.suggestedModel.unanimous) return 'suggested'
      if (!squash(currentName) && this.findCarfaxCatalogueMatch()) return 'carfax'
      return 'current'
    },
    /*
     * The active catalogue row named exactly what CARFAX reported, for CARFAX's year, when CARFAX's own
     * lookup did not resolve it - or null.
     *
     * ⚠ A direct catalogue lookup, not the candidate rows (Codex 2026-10-20). The named finder returns
     * nothing for an exact name by design, so the match hung on the group search having the CARFAX
     * group and series; and a host-supplied list can hold another year or a retired row, which linked
     * the CARFAX row would silently move the year to or apply.
     */
    findCarfaxCatalogueMatch: function () {
      const carfax = this.carfaxMakeModel
      if (!carfax || carfax.modelId > 0) return null
      return findActiveCatalogueModel(this.selectedMake, readMakeModelFields(carfax).model, carfax.productionYear)
    },
    /*
     * ⚠ Always selects, never clears. b-checkbox emits false when an already-ticked box is clicked;
     * honouring that would leave no row chosen and Apply with nothing to apply.
     */
    onChoose: function (key) {
      this.chosenKey = key
    },
    onApply: function () {
      this.$emit('apply', this.chosenRow)
    },
    onCancel: function () {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
  .carfax-model-choices th {
    font-size: 12px;
  }

  .carfax-model-choices td {
    vertical-align: middle;
  }
</style>
